<template>
  <div
    v-if="endDate && hasTimeLeft"
    class="trial-outer-container flex-shrink-0"
    :class="{ 'has-pill': showAutoCancelPill }"
  >
    <div class="trial-inner-container">
      <div class="trial-counter w-full flex flex-col p-1 rounded-xl">
        <div
          v-if="showAutoCancelPill"
          class="auto-cancel-pill flex items-center self-start gap-1 w-max px-2 py-0.5 rounded-full"
          style="background: rgba(239, 68, 68, 0.2);"
        >
          <CalendarIcon class="text-red-500" />
          <BaseText
            type="label"
            size="xs"
            class="text-red-500"
          >
            Auto cancels at end of period
          </BaseText>
        </div>
        <BaseText
          type="body"
          size="sm"
          class="text-neutral-alpha-650 px-2 pb-2 pt-1"
        >
          <span class="font-medium text-white">{{ countdown }}</span>
          {{ message }}
        </BaseText>
        <button
          class="upgrade-btn w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg"
          @click="handleCtaClick"
        >
          <BillingSettingsIcon
            v-if="willAutoCancel"
            class="text-white"
          />
          <UpgradeIcon
            v-else
            class="text-white"
          />
          <BaseText
            type="label"
            size="sm"
            class="text-white"
          >
            {{ willAutoCancel ? 'Manage Plan' : 'Upgrade Foreplay' }}
          </BaseText>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import moment from 'moment'
import { getTrialEndDate } from '../../../utils/trialUtils'
import { SUBSCRIPTION_STATES } from '../../../utils/stripe/productMeta'
// Icons
import UpgradeIcon from '../../globals/Icons/SidebarIcons/UpgradeIcon.vue'
import CalendarIcon from '../../globals/Icons/CalendarIcon.vue'
import BillingSettingsIcon from '../../globals/Icons/SettingsIcons/BillingSettingsIcon.vue'

export default {
  name: 'SubscriptionEndingBanner',
  components: {
    UpgradeIcon,
    CalendarIcon,
    BillingSettingsIcon
  },
  computed: {
    ...mapGetters('AuthModule', ['getUserSubscription', 'getUserFreeTrialDate', 'getTeam', 'isTeamOwner']),
    isTrialing () {
      return this.getUserSubscription?.status === SUBSCRIPTION_STATES.TRIALING
    },
    showAutoCancelPill () {
      return this.isTrialing && this.willAutoCancel
    },
    willAutoCancel () {
      return !!this.getUserSubscription?.cancelAtPeriodEnd
    },
    isCancelling () {
      return !this.isTrialing && this.willAutoCancel
    },
    endDate () {
      // Only solo users or team owners can act on the subscription
      if (this.getTeam && !this.isTeamOwner) return null
      if (this.isTrialing) return getTrialEndDate(this.getUserFreeTrialDate)
      if (this.isCancelling) {
        // currentPeriodEnd is unix seconds (or a Firestore timestamp)
        const periodEnd = this.getUserSubscription.currentPeriodEnd
        const seconds = periodEnd?.seconds ?? periodEnd
        return seconds ? moment.unix(seconds) : null
      }
      return null
    },
    remaining () {
      if (!this.endDate) return null
      return moment.duration(this.endDate.diff(moment(), 'seconds'), 'seconds')
    },
    days () {
      // asDays, not days(): a billing period can run past a month
      return this.remaining ? Math.max(Math.floor(this.remaining.asDays()), 0) : 0
    },
    hours () {
      return this.remaining ? Math.max(this.remaining.hours(), 0) : 0
    },
    minutes () {
      return this.remaining ? Math.max(this.remaining.minutes(), 0) : 0
    },
    hasTimeLeft () {
      return !!this.remaining && this.remaining.asSeconds() > 0
    },
    countdown () {
      if (!this.days && !this.hours) return `${this.minutes} minutes`
      return `${this.days} days ${this.hours} hours`
    },
    message () {
      if (this.isCancelling) return 'left on your subscription. You won\'t be charged again.'
      return this.willAutoCancel
        ? 'left on your trial. Your trial is cancelled and you won\'t be charged.'
        : 'left on your trial. Get in touch with us if you have any questions.'
    }
  },
  methods: {
    ...mapMutations('MiscModule', ['SET_SUBSCRIPTION_DRAWER_VIEW_MODE', 'SET_SETTINGS_DRAWER_VIEW_MODE']),
    handleCtaClick () {
      if (this.willAutoCancel) this.SET_SETTINGS_DRAWER_VIEW_MODE('plan')
      else this.SET_SUBSCRIPTION_DRAWER_VIEW_MODE({ state: true })
    }
  }
}
</script>

<style scoped>
/* Width/opacity transitions key off the parent Sidebar's `.collapsed` class */
.trial-outer-container {
  position: relative;
  width: 100%;
  height: 116px;
  margin-bottom: 8px;
  transition: width var(--collapse-transition-duration) ease-in-out;
}
.trial-outer-container.has-pill {
  height: 144px;
}
.collapsed .trial-outer-container {
  width: 0px;
}
.trial-inner-container {
  position: absolute;
  top: 0px;
  left: 0px;
  width: calc(100% + 20px);
  height: 100%;
  overflow: hidden;
  mask-image: linear-gradient(to left, transparent, black 20px, black);
}
.trial-counter {
  width: 248px;
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.12) 100%), rgba(15, 16, 25, 0.68);
  backdrop-filter: blur(40px);
  transition: opacity var(--collapse-transition-duration) ease-in-out;
}
.collapsed .trial-counter {
  opacity: 0;
}
.auto-cancel-pill {
  background-color: rgba(255, 255, 255, 0.12);
}
.upgrade-btn {
  background-color: rgba(0, 0, 0, 0.24);
  transition: background-color 150ms ease-in-out;
}
.upgrade-btn:hover {
  background-color: rgba(0, 0, 0, 0.32);
}
</style>
